import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CircleAlert, Mail, Send, TriangleAlert } from 'lucide-react';
import {
  EMAIL_KIND_LABEL,
  EMAIL_STATUS_LABEL,
  EmailKind,
  EmailStatus,
  UserRole,
  type EmailListDto,
  type EmailMessageDto,
  type MailStatusDto,
} from '@weloze/shared';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDateTime } from '../../lib/format';
import { useTableSort } from '../../lib/sorting';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Select,
  Skeleton,
  SortHeader,
} from '../../components/ui';

/**
 * `/admin/emails` — o que o portal mandou, e para quem (PLAN §9, fatia k).
 *
 * A tela existe para uma pergunta de auditoria: **o fornecedor foi avisado?**
 * É a mesma razão do registro de download, e a resposta não pode depender de
 * alguém lembrar ter mandado.
 *
 * O topo diz **em que modo o portal está enviando**, antes de qualquer botão.
 * Sem isso, um "Enviado" na lista poderia significar "gravado num arquivo em
 * `./tmp/mail`" — e a pessoa iria embora achando que o fornecedor recebeu.
 */

const STATUS_TONE: Record<EmailStatus, 'success' | 'warning' | 'danger'> = {
  SENT: 'success',
  DRY_RUN: 'warning',
  FAILED: 'danger',
};

export function EmailsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;

  const [data, setData] = useState<EmailListDto | null>(null);
  const [kind, setKind] = useState<EmailKind | ''>('');
  const [status, setStatus] = useState<EmailStatus | ''>('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (kind) params.set('kind', kind);
      if (status) params.set('status', status);
      setData(await api.get<EmailListDto>(`/mail?${params.toString()}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar os e-mails.');
    }
  }, [kind, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const accessors = useMemo(
    () => ({
      sentAt: (row: EmailMessageDto) => row.sentAt,
      kind: (row: EmailMessageDto) => EMAIL_KIND_LABEL[row.kind],
      supplier: (row: EmailMessageDto) => row.supplierName ?? '',
      status: (row: EmailMessageDto) => EMAIL_STATUS_LABEL[row.status],
    }),
    [],
  );

  const { rows, sort, toggle } = useTableSort(data?.messages ?? [], accessors, {
    key: 'sentAt',
    direction: 'desc',
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">E-mails</h1>
        <p className="mt-1 text-sm text-slate-600">
          O que o portal enviou, para quem e quando. É o que responde “o fornecedor foi avisado?”
          sem depender de ninguém lembrar.
        </p>
      </header>

      {data && <ModeCard status={data.status} isAdmin={isAdmin} onDone={load} />}

      {error && <Alert>{error}</Alert>}

      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Tipo"
          name="kind"
          className="w-56"
          value={kind}
          onChange={(e) => setKind(e.target.value as EmailKind | '')}
        >
          <option value="">Todos</option>
          {(Object.keys(EMAIL_KIND_LABEL) as EmailKind[]).map((value) => (
            <option key={value} value={value}>
              {EMAIL_KIND_LABEL[value]}
            </option>
          ))}
        </Select>
        <Select
          label="Situação"
          name="status"
          className="w-44"
          value={status}
          onChange={(e) => setStatus(e.target.value as EmailStatus | '')}
        >
          <option value="">Todas</option>
          {(Object.keys(EMAIL_STATUS_LABEL) as EmailStatus[]).map((value) => (
            <option key={value} value={value}>
              {EMAIL_STATUS_LABEL[value]}
            </option>
          ))}
        </Select>
      </div>

      {!data ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nenhum e-mail registrado"
          description={
            kind || status
              ? 'Troque ou limpe os filtros para ver os demais.'
              : 'O portal ainda não enviou nada. A primeira RNC emitida ou a primeira competência publicada aparecem aqui.'
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-medium">
                  <SortHeader column="sentAt" sort={sort} onSort={toggle}>
                    Quando
                  </SortHeader>
                </th>
                <th className="px-4 py-2 font-medium">
                  <SortHeader column="kind" sort={sort} onSort={toggle}>
                    Tipo
                  </SortHeader>
                </th>
                <th className="px-4 py-2 font-medium">
                  <SortHeader column="supplier" sort={sort} onSort={toggle}>
                    Fornecedor
                  </SortHeader>
                </th>
                <th className="px-4 py-2 font-medium">Destinatários</th>
                <th className="px-4 py-2 font-medium">
                  <SortHeader column="status" sort={sort} onSort={toggle}>
                    Situação
                  </SortHeader>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id} className="align-top">
                  <td className="whitespace-nowrap px-4 py-2 text-slate-600">
                    {formatDateTime(row.sentAt)}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {EMAIL_KIND_LABEL[row.kind]}
                    {row.nonconformityId && row.nonconformityNumber && (
                      <Link
                        to={`/rncs/${row.nonconformityId}`}
                        className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700 underline-offset-4 hover:underline"
                      >
                        {row.nonconformityNumber}
                      </Link>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {row.storeId && row.supplierName ? (
                      <Link
                        to={`/admin/fornecedores/${row.storeId}`}
                        className="text-brand-700 underline-offset-4 hover:underline"
                      >
                        {row.supplierName}
                      </Link>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  {/*
                    Os endereços reais, mesmo quando o desvio de teste mandou
                    para outro lugar: a pergunta de auditoria é quem deveria
                    receber, e a resposta não pode mudar com uma variável de
                    ambiente.
                  */}
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {row.recipients.join(', ')}
                  </td>
                  <td className="px-4 py-2">
                    <Badge tone={STATUS_TONE[row.status]}>{EMAIL_STATUS_LABEL[row.status]}</Badge>
                    {row.error && (
                      <p className="mt-1 max-w-md text-xs text-red-700">{row.error}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * O modo de envio, e os disparos manuais.
 *
 * Amarelo em ensaio, e não verde: enquanto o `MAIL_DRY_RUN` estiver ligado,
 * nada chega a fornecedor nenhum, e a tela precisa dizer isso do jeito mais
 * chato possível.
 */
function ModeCard({
  status,
  isAdmin,
  onDone,
}: {
  status: MailStatusDto;
  isAdmin: boolean;
  onDone: () => void;
}) {
  const [para, setPara] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  async function acao(nome: string, executar: () => Promise<string>) {
    setOcupado(nome);
    setErro(null);
    setAviso(null);
    try {
      setAviso(await executar());
      onDone();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível concluir.');
    } finally {
      setOcupado(null);
    }
  }

  async function testar(event: FormEvent) {
    event.preventDefault();
    await acao('teste', async () => {
      const r = await api.post<{ status: string; error: string | null }>('/mail/test', { to: para });
      return r.status === 'SENT'
        ? `Enviado para ${para}. Se não chegar em alguns minutos, confira o spam e a Application Access Policy.`
        : r.status === 'DRY_RUN'
          ? 'Gravado em disco: o portal está em modo de ensaio.'
          : `Falhou: ${r.error ?? 'sem detalhe'}`;
    });
  }

  return (
    <Card
      className={
        status.sending ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'
      }
    >
      <div className="flex items-start gap-3">
        {status.sending ? (
          <Mail className="mt-0.5 size-5 shrink-0 text-emerald-600" aria-hidden />
        ) : (
          <TriangleAlert className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
        )}
        <div className="min-w-0 flex-1">
          <p className={status.sending ? 'font-medium text-emerald-900' : 'font-medium text-amber-900'}>
            {status.sending ? 'Enviando de verdade' : 'Modo de ensaio — nada sai daqui'}
          </p>
          <p className="mt-1 text-sm text-slate-700">{status.reason}</p>
          <p className="mt-2 text-xs text-slate-500">
            Alertas e resumo diários em <code>{status.dailyCron}</code> · mesmo alerta só se repete
            depois de {status.alertCooldownDays} dias
            {status.recipientOverride && ` · desvio de teste para ${status.recipientOverride}`}
          </p>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-4 space-y-3 border-t border-slate-200/70 pt-4">
          {erro && <Alert>{erro}</Alert>}
          {aviso && (
            <p className="flex items-start gap-2 text-sm text-slate-700">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden />
              {aviso}
            </p>
          )}

          <form onSubmit={testar} className="flex flex-wrap items-end gap-2">
            <Field
              label="Enviar um teste para"
              name="teste"
              type="email"
              className="min-w-[16rem] flex-1"
              placeholder="seu.email@weloze.com.br"
              hint="Confirma a configuração do Graph sem tocar em nenhum fornecedor."
              value={para}
              onChange={(e) => setPara(e.target.value)}
            />
            <Button type="submit" variant="outline" loading={ocupado === 'teste'} disabled={!para}>
              <Send className="size-4" aria-hidden />
              Enviar teste
            </Button>
          </form>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              loading={ocupado === 'alertas'}
              onClick={() =>
                void acao('alertas', async () => {
                  const r = await api.post<{ lojas: number; enviados: number }>('/mail/alerts');
                  return `${r.enviados} de ${r.lojas} lojas receberam alerta. As demais não têm pendência, não têm contato, ou já foram avisadas nos últimos ${status.alertCooldownDays} dias.`;
                })
              }
            >
              Rodar os alertas agora
            </Button>
            <Button
              variant="outline"
              size="sm"
              loading={ocupado === 'digest'}
              onClick={() =>
                void acao('digest', async () => {
                  const r = await api.post<{ status: string }>('/mail/digest');
                  return r.status === 'FAILED'
                    ? 'Não foi possível enviar o resumo.'
                    : 'Resumo enviado aos administradores.';
                })
              }
            >
              Enviar o resumo agora
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
